import { SitterPage } from './components/CareView.jsx';
import { useEffect, useRef, useState } from 'react';
import { api } from './api.js';
import { Loading } from './components/Loading.jsx';
import { AuthScreen } from './components/AuthScreen.jsx';
import { AppShell } from './components/AppShell.jsx';
import { FeedbackButton } from './components/Feedback.jsx';
import { OnboardingFlow } from './components/OnboardingFlow.jsx';
import { ClaimDevicePage } from './components/ClaimDevicePage.jsx';
import { DeviceHelpPage } from './components/DeviceHelpPage.jsx';
import { takeAfterOnboarding } from './after-onboarding.js';
import { ConfirmProvider } from './components/ConfirmDialog.jsx';
import { track } from './events.js';
import { safeNextPath } from '../../shared/next-path.js';

// The /app SPA serves a single page; /app/claim is the QR-scan landing
// route for device claim. Honour it even mid-onboarding — a user with a
// new device shouldn't have to finish a wizard before linking.
function isClaimRoute() {
  if (typeof window === 'undefined') return false;
  return /\/app\/claim\/?$/.test(window.location.pathname);
}

// A display's SETUP > HELP code lands here, signed in or not.
function isDeviceHelpRoute() {
  if (typeof window === 'undefined') return false;
  return /\/app\/device-help\/?$/.test(window.location.pathname);
}

function AppInner() {
  // undefined = loading, null = not signed in, object = signed in.
  const [me, setMe] = useState(undefined);
  // Tracks the current surface so feedback carries the right context.
  const [page, setPage] = useState('sign-in');

  const refreshMe = () =>
    api('/api/auth/me')
      .then((r) => setMe(r.user))
      .catch(() => setMe(null));

  useEffect(() => {
    track('app.open', 'app');
    refreshMe();
  }, []);

  // A pairing QR or a tank invite sends a signed-out visitor to
  // /app/?next=<path>. Once signed in (a password sign-in reloads this
  // page with the query intact), carry on to where they were going. The
  // email-link sign-in does the same on the server.
  const nextPath =
    me && typeof window !== 'undefined'
      ? safeNextPath(new URLSearchParams(window.location.search).get('next'))
      : null;
  useEffect(() => {
    if (nextPath) window.location.replace(nextPath);
  }, [nextPath]);

  // A brand-new keeper who scanned a display's LINK code is sent through tank
  // setup first (the link page has no tank to offer them); once that finishes,
  // carry on to linking the display instead of leaving them on Home.
  const afterRef = useRef(undefined);
  if (afterRef.current === undefined && me && me.onboarding_completed_at && !nextPath) {
    afterRef.current = takeAfterOnboarding();
  }
  const afterOnboarding = afterRef.current || null;
  useEffect(() => {
    if (afterOnboarding) window.location.replace(afterOnboarding);
  }, [afterOnboarding]);

  useEffect(() => {
    if (me === null) setPage('sign-in');
    else if (me && !me.onboarding_completed_at) setPage('onboarding');
  }, [me]);

  if (me === undefined || nextPath || afterOnboarding) return <Loading />;

  if (isDeviceHelpRoute()) {
    return <DeviceHelpPage user={me} />;
  }

  if (isClaimRoute()) {
    return <ClaimDevicePage user={me} />;
  }

  if (me === null)
    return (
      <div className="fb-no-nav">
        <AuthScreen />
        <FeedbackButton page={page} userEmail={null} />
      </div>
    );
  // Signed in but hasn't finished onboarding — route into the flow. It
  // sets onboarding_completed_at on completion; we refresh me so the
  // next render hits AppShell.
  if (!me.onboarding_completed_at) {
    return <OnboardingFlow user={me} onComplete={refreshMe} />;
  }
  return (
    <>
      <AppShell
        user={me}
        onSignOut={() => setMe(null)}
        onPageChange={setPage}
        onRefreshUser={refreshMe}
      />
      <FeedbackButton page={page} userEmail={me?.email || null} dockOnPhone />
    </>
  );
}

export default function App() {
  if (/\/app\/sitter\/?$/.test(window.location.pathname)) return <SitterPage />;
  return (
    <ConfirmProvider>
      <AppInner />
    </ConfirmProvider>
  );
}
